// La sémantique des écarts, en un seul endroit.
//
// RÈGLE FONDATRICE : un écart n'est JAMAIS vert, et sa couleur ne dépend
// JAMAIS de son signe.
//
// Le code disait l'inverse à deux endroits — `op.diff < 0 ? stop : ok` — ce qui
// affichait un excédent de caisse en vert. Un excédent de 50 € n'est pas une
// bonne nouvelle : c'est la même anomalie qu'un manque de 50 €, et souvent la
// même cause. Le vert ne dit qu'une chose : « il n'y a pas d'écart ».
//
// L'échelle est celle de la V2, et elle est la même partout : moins de 2 €
// toléré, 2 à 5 € à surveiller, 5 € et plus critique.

import type { Couleur } from '@/ui/primitives'

export const SEUIL_TOLERE = 2
export const SEUIL_CRITIQUE = 5
/** En dessous, c'est un arrondi de centime, pas un écart. */
export const EPSILON = 0.005

export interface Bandes { tolere: number; critique: number }

/**
 * Les bornes en vigueur. Réglables depuis le back-office : ce sont des seuils de
 * VIGILANCE, pas des règles de calcul — les déplacer ne change aucun chiffre,
 * seulement le moment où l'écran hausse le ton.
 *
 * Elles vivent dans une variable de module plutôt que passées en paramètre :
 * une trentaine d'appels les utilisent, et les threader partout ferait porter à
 * chaque écran une décision qui n'est pas la sienne. `poserBandes` est appelée
 * une fois, au chargement des réglages.
 */
let BANDES: Bandes = { tolere: SEUIL_TOLERE, critique: SEUIL_CRITIQUE }

export function poserBandes(b: Bandes): void {
  // Des bornes incohérentes rendraient la bande « à surveiller » vide sans
  // qu'aucun écran ne le signale : on garde les précédentes.
  if (!(b.tolere > 0) || !(b.critique > b.tolere)) return
  BANDES = { tolere: b.tolere, critique: b.critique }
}

export const bandes = (): Bandes => BANDES

export type Severite = 'nul' | 'tolere' | 'surveille' | 'critique'

export function severite(montant: number, seuils: Bandes = BANDES): Severite {
  const a = Math.abs(Number(montant) || 0)
  if (a < EPSILON) return 'nul'
  if (a < seuils.tolere) return 'tolere'
  if (a < seuils.critique) return 'surveille'
  return 'critique'
}

/**
 * La couleur d'un écart. Le vert est réservé au zéro ; un écart toléré est
 * NEUTRE, pas vert — il ne mérite pas qu'on s'y arrête, mais il ne mérite pas
 * non plus d'être félicité.
 */
export function couleurEcart(montant: number, seuils?: Bandes): string {
  switch (severite(montant, seuils)) {
    case 'nul':        return 'var(--ok)'
    case 'tolere':     return 'var(--ink3)'
    case 'surveille':  return 'var(--warn)'
    case 'critique':   return 'var(--stop)'
  }
}

export function lampeEcart(montant: number, seuils?: Bandes): Couleur {
  switch (severite(montant, seuils)) {
    case 'nul':        return 'ok'
    case 'tolere':     return 'neutre'
    case 'surveille':  return 'warn'
    case 'critique':   return 'stop'
  }
}

/**
 * Un écart s'écrit toujours avec son signe : « +12,40 € » et « −12,40 € » ne
 * disent pas la même chose au comptable, même s'ils pèsent pareil à l'écran.
 */
export function fmtEcart(montant: number): string {
  const n = Number(montant) || 0
  if (Math.abs(n) < EPSILON) return '0,00 €'
  const signe = n > 0 ? '+' : '−'
  return signe + Math.abs(n).toLocaleString('fr-FR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }) + ' €'
}

/** Le mot qui va avec la couleur — pour les infobulles et les lecteurs d'écran. */
export function motEcart(montant: number, seuils?: Bandes): string {
  switch (severite(montant, seuils)) {
    case 'nul':        return 'conforme'
    case 'tolere':     return 'écart toléré'
    case 'surveille':  return 'écart à surveiller'
    case 'critique':   return 'écart critique'
  }
}

/** Écart en NOMBRE d'unités (enveloppes, tickets) — mêmes règles, autre échelle. */
export function couleurEcartUnites(n: number): string {
  const a = Math.abs(Number(n) || 0)
  if (a === 0) return 'var(--ok)'
  return a === 1 ? 'var(--warn)' : 'var(--stop)'
}

/** La même échelle, en <Lamp>. Quatre écrans la redérivaient à la main. */
export function lampeEcartUnites(n: number): Couleur {
  const a = Math.abs(Number(n) || 0)
  return a === 0 ? 'ok' : a === 1 ? 'warn' : 'stop'
}

export function fmtEcartUnites(n: number, singulier: string, pluriel = singulier + 's'): string {
  const a = Math.abs(Number(n) || 0)
  if (a === 0) return 'concordance'
  const mot = a > 1 ? pluriel : singulier
  return `${n > 0 ? '+' : '−'}${a} ${mot}`
}
